import React from 'react';
import { motion } from 'framer-motion';
import { UserPlus, QrCode, Send } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const HowItWorks = () => {
  const { language, t } = useLanguage();

  const steps = [
    {
      icon: UserPlus,
      title: t('how_step1_title'),
      description: t('how_step1_desc'),
      number: '01',
    },
    {
      icon: QrCode,
      title: t('how_step2_title'),
      description: t('how_step2_desc'),
      number: '02',
    },
    {
      icon: Send,
      title: t('how_step3_title'),
      description: t('how_step3_desc'),
      number: '03',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section id="how-it-works" className="section-spacing bg-slate-900 text-white relative overflow-hidden" data-testid="how-section">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.05)_1px,transparent_1px)] bg-[size:60px_60px]" />
      
      {/* Glow effects */}
      <div className="absolute top-0 start-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 end-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />

      <div className="container-custom relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 text-emerald-400 text-sm font-medium mb-4">
            {t('how_badge')}
          </span>
          <h2 className="text-3xl md:text-5xl font-bold mb-4" data-testid="how-title">
            {t('how_title')}{' '}
            <span className="text-emerald-400">{t('how_title_highlight')}</span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {t('how_subtitle')}
          </p>
        </motion.div>

        {/* Steps */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 relative"
        >
          {/* Connecting line */}
          <div className="hidden md:block absolute top-24 start-[calc(16.67%+2rem)] end-[calc(16.67%+2rem)] h-0.5 bg-gradient-to-l from-emerald-500 via-emerald-500/50 to-emerald-500" />

          {steps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="relative text-center"
                data-testid={`how-step-${index}`}
              >
                {/* Number badge */}
                <div className="absolute -top-4 start-1/2 -translate-x-1/2 text-6xl font-bold text-emerald-500/10">
                  {step.number}
                </div>

                {/* Icon */}
                <div className="relative mx-auto w-20 h-20 rounded-2xl bg-emerald-500/20 flex items-center justify-center mb-6 border border-emerald-500/30">
                  <IconComponent className="w-10 h-10 text-emerald-400" />
                  {/* Dot indicator */}
                  <div className="absolute -bottom-2 start-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-emerald-500 border-4 border-slate-900" />
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                <p className="text-slate-400 leading-relaxed">{step.description}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Demo QR Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-20 flex justify-center"
        >
          <div className="glass-card-dark p-8 rounded-3xl text-center max-w-md">
            <div className="w-32 h-32 mx-auto bg-white rounded-2xl p-3 mb-6">
              <div className="w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMSAyMSI+PHBhdGggZD0iTTEgMWg3djdIMXptMiAydjNoM1Yzem0xMi03aDd2N2gtN3ptMiAydjNoM1Yzek0xIDEzaDd2N0gxem0yIDJ2M2gzdi0zem0xMS01aDN2M2gtM3ptMCAzaDN2M2gtM3ptLTMgMGgzdjNoLTN6bTAgM2gzdjNoLTN6bTMgMGgzdjNoLTN6TTQgNGgxdjFINHptMCAxMGgxdjFINHptMTAtMTBoMXYxaC0xeiIvPjwvc3ZnPg==')] bg-contain" />
            </div>
            <h4 className="text-lg font-bold mb-2">
              {language === 'ar' ? 'جرب المسح الآن' : 'Try Scanning Now'}
            </h4>
            <p className="text-slate-400 text-sm">
              {language === 'ar' 
                ? 'امسح هذا الكود للتجربة (نسخة تجريبية)'
                : 'Scan this code to try (demo version)'}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
